import { useState, useMemo, useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { format } from 'date-fns';
import { BaseModal } from '@anyroad/modal';
import { DateSelect } from '@anyroad/date-select';
import {
  useListExperienceTimeSlots,
  useRescheduleBooking,
  useDiffGuestBookingPricing,
} from '@anyroad/api';
import {
  BookingUpdated,
  PaymentDetails,
  CurrentBookingDisplay,
  TimeSlotSelection,
} from './components';
import {
  parseBookingDate,
  buildPricingRulesMap,
  findCurrentBookingSlotId,
  isSaveButtonDisabled,
} from './helpers';
import {
  usePricingDiffHandler,
  useCurrentBookingInfo,
  useAvailableTimeSlots,
} from './hooks';
import type {
  PaymentDetailsResult,
  RescheduleStep,
  RescheduleModalProps,
} from './types';
import {
  RescheduleModalContainer,
  RescheduleModalHeader,
  ButtonContainerStyle,
  ColumnsContainer,
  RescheduleBookingColumn,
  ColumnHeader,
  SaveButton,
  Label,
  DateSelectRow,
  TimeSelectRow,
  ConfirmButton,
} from './styles';

export const RescheduleModalContent = ({
  availableDates,
  booking,
  isoTime,
  onClose,
  rescheduleStepRef,
}: RescheduleModalProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(() =>
    parseBookingDate(booking.date || booking.formattedDate)
  );
  const [selectedTimeId, setSelectedTimeId] = useState<string | undefined>(
    undefined
  );
  const [step, setStep] = useState<RescheduleStep>('form');

  useEffect(() => {
    if (rescheduleStepRef) rescheduleStepRef.current = step;
  }, [step, rescheduleStepRef]);
  const [paymentDetailsResult, setPaymentDetailsResult] =
    useState<PaymentDetailsResult | null>(null);
  const [diffMutationContext, setDiffMutationContext] = useState<{
    date: string;
    time: string;
  } | null>(null);

  const { currentBookingDateStr, currentBookingTime24 } =
    useCurrentBookingInfo(booking);

  const rescheduleMutation = useRescheduleBooking({
    mutation: {
      onSuccess: () => {
        setStep('booking-updated');
      },
    },
  });

  const handlePricingDiffSuccess = usePricingDiffHandler({
    rescheduleMutation,
    diffMutationContext,
    setPaymentDetailsResult,
    setStep,
  });

  const diffMutation = useDiffGuestBookingPricing({
    mutation: {
      onSuccess: handlePricingDiffSuccess,
    },
  });

  const formattedDate = useMemo(() => {
    return selectedDate ? format(selectedDate, 'yyyy-MM-dd') : undefined;
  }, [selectedDate]);

  useEffect(() => {
    rescheduleMutation.reset();
  }, [selectedDate, selectedTimeId]);

  const {
    data: timeSlotsData,
    isLoading: isLoadingTimeSlots,
    isError: isTimeSlotsError,
  } = useListExperienceTimeSlots(booking?.tour?.id, { date: formattedDate });

  // Filter and transform available time slots based on reschedule cutoff
  const minutesBeforeCutoff =
    booking.tour.minutes_before_reschedule_cutoff ?? 1440;
  const availableTimes = useAvailableTimeSlots(
    timeSlotsData,
    minutesBeforeCutoff
  );

  // Find the ID of the current booking's time slot (if viewing same date)
  const currentSlotId = useMemo(
    () =>
      findCurrentBookingSlotId({
        timeSlots: timeSlotsData?.items,
        currentBookingDateStr,
        selectedDateStr: formattedDate,
        currentBookingTime24,
      }),
    [
      currentBookingDateStr,
      formattedDate,
      currentBookingTime24,
      timeSlotsData?.items,
    ]
  );

  useEffect(() => {
    if (!availableTimes.length) return;
    const selectionStillValid =
      selectedTimeId && availableTimes.some((t) => t.id === selectedTimeId);
    if (availableTimes.length === 1) {
      setSelectedTimeId(availableTimes[0].id);
      return;
    }
    if (
      formattedDate === currentBookingDateStr &&
      currentSlotId &&
      availableTimes.some((t) => t.id === currentSlotId) &&
      !selectionStillValid
    ) {
      setSelectedTimeId(currentSlotId ?? undefined);
    }
  }, [
    availableTimes,
    formattedDate,
    currentBookingDateStr,
    currentSlotId,
    selectedTimeId,
  ]);

  const handleSaveChanges = useCallback(() => {
    const guestBookingId = booking.id_hash;
    if (!selectedDate || !selectedTimeId || !guestBookingId) return;
    const selectedSlot = availableTimes.find((t) => t.id === selectedTimeId);
    if (!selectedSlot) return;
    setDiffMutationContext({
      date: selectedSlot.date,
      time: selectedSlot.time,
    });
    diffMutation.mutate({
      guestBookingId,
      data: {
        time_slot_id: Number(selectedTimeId),
      },
    });
  }, [
    selectedDate,
    selectedTimeId,
    booking.id_hash,
    availableTimes,
    diffMutation,
  ]);

  const handleConfirmPaymentDetails = useCallback(() => {
    const guestBookingId = booking.id_hash;
    if (!selectedTimeId || !guestBookingId) return;
    const selectedSlot = availableTimes.find((t) => t.id === selectedTimeId);
    if (!selectedSlot) return;
    rescheduleMutation.mutate({
      bookingId: guestBookingId,
      data: {
        booking: {
          date: selectedSlot.date,
          time: selectedSlot.time,
        },
      },
    });
  }, [selectedTimeId, booking.id_hash, availableTimes, rescheduleMutation]);

  const pricingRulesMap = useMemo(() => {
    const effectiveSlotId =
      selectedTimeId ??
      (formattedDate === currentBookingDateStr ? currentSlotId : null);

    return buildPricingRulesMap(timeSlotsData?.items, effectiveSlotId);
  }, [
    timeSlotsData?.items,
    selectedTimeId,
    formattedDate,
    currentBookingDateStr,
    currentSlotId,
  ]);

  const noTimesOnBookingDate = useMemo(() => {
    return (
      availableTimes.length === 1 &&
      formattedDate === currentBookingDateStr &&
      availableTimes[0].id === currentSlotId
    );
  }, [availableTimes, formattedDate, currentBookingDateStr, currentSlotId]);

  const isSaveDisabled = isSaveButtonDisabled({
    selectedDate,
    selectedTimeId,
    bookingIdHash: booking.id_hash,
    currentSlotId,
    isDiffPending: diffMutation.isPending,
    isReschedulePending: rescheduleMutation.isPending,
  });
  // Booking Updated success screen
  if (step === 'booking-updated') {
    return <BookingUpdated booking={booking} onClose={onClose} />;
  }

  // Payment Details (amount due or refund)
  if (step === 'payment-details' && paymentDetailsResult) {
    return (
      <PaymentDetails
        booking={booking}
        isRefund={paymentDetailsResult.type === 'refund'}
        paymentDetailsResult={paymentDetailsResult}
        rescheduleMutation={rescheduleMutation}
        handleConfirmPaymentDetails={handleConfirmPaymentDetails}
        handleBackToReschedule={() => setStep('form')}
      />
    );
  }

  return (
    <RescheduleModalContainer data-testid="reschedule-modal">
      <RescheduleModalHeader>
        <FormattedMessage
          id="MODIFY_BOOKING"
          defaultMessage="Modify booking"
        />{' '}
      </RescheduleModalHeader>
      <ColumnsContainer>
        <CurrentBookingDisplay booking={booking} />
        <RescheduleBookingColumn>
          <ColumnHeader>
            <FormattedMessage defaultMessage="Change to:" />
          </ColumnHeader>
          <Label>
            <FormattedMessage id="START_DATE" defaultMessage="Start Date" />
          </Label>
          <DateSelectRow>
            <DateSelect
              availableDates={availableDates}
              selected={selectedDate}
              onChange={(date: Date | null) => {
                setSelectedDate(date);
                setSelectedTimeId(undefined);
              }}
              dateFormat="MMM d, yyyy"
              placeholderText="Select start date"
              dataTestId="start-date-select"
            />
          </DateSelectRow>
          <TimeSelectRow>
            {!noTimesOnBookingDate && (
              <Label>
                <FormattedMessage defaultMessage="Start Time" />
              </Label>
            )}
            <TimeSlotSelection
              availableTimes={availableTimes}
              selectedTimeId={selectedTimeId}
              onTimeSlotChange={setSelectedTimeId}
              numberOfGuests={booking.numberOfGuests}
              guestsByTicketType={booking.guestsByTicketType}
              isCapacityByTicketType={booking.isCapacityByTicketType}
              isoTime={isoTime}
              pricingRulesMap={pricingRulesMap}
              isLoadingTimeSlots={isLoadingTimeSlots}
              isTimeSlotsError={isTimeSlotsError}
              hasRescheduleMutationError={rescheduleMutation.isError}
              selectedDate={selectedDate}
              noTimesOnBookingDate={noTimesOnBookingDate}
            />
          </TimeSelectRow>
        </RescheduleBookingColumn>
      </ColumnsContainer>
      <SaveButton>
        <ButtonContainerStyle>
          <ConfirmButton
            data-testid="continue-reschedule-changes"
            disabled={isSaveDisabled}
            loading={diffMutation.isPending || rescheduleMutation.isPending}
            onClick={() => {
              if (diffMutation.isPending || rescheduleMutation.isPending)
                return;
              handleSaveChanges();
            }}
          >
            <FormattedMessage id="CONTINUE" defaultMessage="Continue" />
          </ConfirmButton>
        </ButtonContainerStyle>
      </SaveButton>
    </RescheduleModalContainer>
  );
};

export const RescheduleModal = BaseModal<RescheduleModalProps>(
  RescheduleModalContent,
  {
    padding: '32px 32px',
    isBackgroundClickable: false,
  }
);
